import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { API } from "@/contexts/AuthContext";
import {
  Drop,
  Plus,
  Trash,
  FilePdf,
  FileText,
  ListMagnifyingGlass,
  CaretRight,
  Funnel,
  X,
  Check,
  Archive,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const emptyFilters = {
  fieldId: "",
  kultur: "",
  verwendungsart: "",
  dateFrom: "",
  dateTo: "",
};

export default function ApplicationsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState(emptyFilters);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/app/applications`);
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const uniqueFields = useMemo(() => {
    const map = new Map();
    for (const a of items) {
      if (a.field_id && !map.has(a.field_id)) {
        map.set(a.field_id, a.field_name || a.field_id);
      }
    }
    return Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) =>
      a.name.localeCompare(b.name, "de")
    );
  }, [items]);

  const uniqueKulturen = useMemo(() => {
    return Array.from(new Set(items.map((a) => a.kultur).filter(Boolean))).sort();
  }, [items]);

  const activeFilterCount = useMemo(
    () => Object.values(filters).filter(Boolean).length,
    [filters]
  );

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return items.filter((a) => {
      if (
        q &&
        !(
          a.product_name?.toLowerCase().includes(q) ||
          a.field_name?.toLowerCase().includes(q) ||
          a.active_ingredient?.toLowerCase().includes(q) ||
          a.target_pest?.toLowerCase().includes(q)
        )
      )
        return false;
      if (filters.fieldId && a.field_id !== filters.fieldId) return false;
      if (filters.kultur && a.kultur !== filters.kultur) return false;
      if (filters.verwendungsart && a.verwendungsart !== filters.verwendungsart)
        return false;
      if (filters.dateFrom && a.applied_at) {
        if (new Date(a.applied_at) < new Date(filters.dateFrom)) return false;
      }
      if (filters.dateTo && a.applied_at) {
        const to = new Date(filters.dateTo);
        to.setHours(23, 59, 59, 999);
        if (new Date(a.applied_at) > to) return false;
      }
      return true;
    });
  }, [items, search, filters]);

  const allSelected =
    filtered.length > 0 && filtered.every((a) => selected.has(a.id));

  const toggleSelectAll = () => {
    setSelected((prev) => {
      if (allSelected) {
        const next = new Set(prev);
        filtered.forEach((a) => next.delete(a.id));
        return next;
      }
      const next = new Set(prev);
      filtered.forEach((a) => next.add(a.id));
      return next;
    });
  };

  const toggleSelected = (id, e) => {
    e.stopPropagation();
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const onDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Aufzeichnung wirklich löschen?")) return;
    await axios.delete(`${API}/app/applications/${id}`);
    toast.success("Aufzeichnung gelöscht");
    setSelected((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    load();
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`${selected.size} Aufzeichnung(en) löschen?`)) return;
    try {
      await Promise.all(
        Array.from(selected).map((id) =>
          axios.delete(`${API}/app/applications/${id}`)
        )
      );
      toast.success(`${selected.size} Aufzeichnung(en) gelöscht`);
      setSelected(new Set());
      load();
    } catch {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const exportPdf = async () => {
    const ids = selected.size > 0 ? `&ids=${Array.from(selected).join(",")}` : "";
    const res = await axios.get(`${API}/app/export?format=pdf${ids}`, {
      responseType: "blob",
    });
    const stamp = new Date().toISOString().slice(0, 10);
    const count = selected.size > 0 ? `-auswahl${selected.size}` : "";
    downloadBlob(
      new Blob([res.data], { type: "application/pdf" }),
      `plantus-psm${count}-${stamp}.pdf`
    );
    toast.success(
      selected.size > 0
        ? `PDF mit ${selected.size} Anwendung(en) heruntergeladen`
        : "PDF heruntergeladen"
    );
  };

  const exportJson = async () => {
    const ids = selected.size > 0 ? `&ids=${Array.from(selected).join(",")}` : "";
    const { data } = await axios.get(`${API}/app/export?format=json${ids}`);
    const stamp = new Date().toISOString().slice(0, 10);
    const count = selected.size > 0 ? `-auswahl${selected.size}` : "";
    downloadBlob(
      new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }),
      `plantus-psm${count}-${stamp}.json`
    );
    toast.success(
      selected.size > 0
        ? `JSON mit ${selected.size} Anwendung(en) heruntergeladen`
        : "JSON heruntergeladen"
    );
  };

  const [backupLoading, setBackupLoading] = useState(false);
  const fullBackup = async () => {
    setBackupLoading(true);
    try {
      const res = await axios.get(`${API}/app/backup`, { responseType: "blob" });
      const stamp = new Date().toISOString().slice(0, 16).replace("T", "_").replace(":", "-");
      downloadBlob(
        new Blob([res.data], { type: "application/zip" }),
        `plantus-backup-${stamp}.zip`
      );
      toast.success("Vollständiges Backup heruntergeladen");
    } catch {
      toast.error("Backup fehlgeschlagen");
    } finally {
      setBackupLoading(false);
    }
  };

  const resetFilters = () => {
    setFilters(emptyFilters);
    setSearch("");
  };

  return (
    <div className="max-w-6xl" data-testid="applications-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-3xl font-semibold text-forest-900">
            Anwendungen
          </h1>
          <p className="mt-2 font-body text-[15px] text-[#4A524C]">
            Alle erfassten PSM-Anwendungen. Konform mit EU 2023/564.
          </p>
        </div>
        <div className="flex gap-3 flex-wrap">
          <button
            onClick={exportPdf}
            data-testid="export-pdf"
            className="inline-flex items-center gap-2 bg-white border border-bone-200 text-forest-700 hover:border-forest-600/40 px-4 py-2.5 rounded-xl font-medium text-sm transition"
          >
            <FilePdf size={18} weight="duotone" />
            PDF{selected.size > 0 ? ` (${selected.size})` : ""}
          </button>
          <button
            onClick={exportJson}
            data-testid="export-json"
            className="inline-flex items-center gap-2 bg-white border border-bone-200 text-forest-700 hover:border-forest-600/40 px-4 py-2.5 rounded-xl font-medium text-sm transition"
          >
            <FileText size={18} weight="duotone" />
            JSON{selected.size > 0 ? ` (${selected.size})` : ""}
          </button>
          <button
            onClick={fullBackup}
            disabled={backupLoading}
            data-testid="export-backup"
            title="Kompletter Datenexport als ZIP (DSGVO-konform)"
            className="inline-flex items-center gap-2 bg-white border border-bone-200 text-forest-700 hover:border-forest-600/40 disabled:opacity-60 px-4 py-2.5 rounded-xl font-medium text-sm transition"
          >
            <Archive size={18} weight="duotone" />
            {backupLoading ? "Erstelle …" : "Backup (ZIP)"}
          </button>
          <Link
            to="/app/applications/new"
            data-testid="apps-new"
            className="inline-flex items-center gap-2 bg-forest-600 hover:bg-forest-700 text-white font-medium px-4 py-2.5 rounded-xl transition"
          >
            <Plus size={16} weight="bold" /> Neue Anwendung
          </Link>
        </div>
      </div>

      {/* Suche + Filter Toggle */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 min-w-[260px]">
          <ListMagnifyingGlass
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7A827C]"
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Mittel, Wirkstoff, Fläche, Schaderreger suchen …"
            data-testid="apps-filter"
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-bone-200 bg-white text-sm focus:outline-none focus:border-forest-600 focus:ring-2 focus:ring-forest-600/15"
          />
        </div>
        <button
          onClick={() => setShowFilters((v) => !v)}
          data-testid="apps-toggle-filters"
          className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border font-medium text-sm transition ${
            activeFilterCount > 0 || showFilters
              ? "bg-forest-600/10 border-forest-600/30 text-forest-700"
              : "bg-white border-bone-200 text-forest-700 hover:border-forest-600/40"
          }`}
        >
          <Funnel size={16} weight={activeFilterCount > 0 ? "fill" : "regular"} />
          Filter
          {activeFilterCount > 0 && (
            <span className="ml-1 bg-forest-600 text-white text-xs px-1.5 py-0.5 rounded-full font-semibold">
              {activeFilterCount}
            </span>
          )}
        </button>
        {(activeFilterCount > 0 || search) && (
          <button
            onClick={resetFilters}
            data-testid="apps-reset-filters"
            className="text-sm text-[#7A827C] hover:text-forest-700 transition inline-flex items-center gap-1"
          >
            <X size={14} /> Zurücksetzen
          </button>
        )}
      </div>

      {/* Filter-Panel */}
      {showFilters && (
        <div
          className="bg-white rounded-2xl border border-bone-200 p-5 mb-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
          data-testid="apps-filter-panel"
        >
          <FilterSelect
            label="Fläche"
            value={filters.fieldId}
            onChange={(v) => setFilters((f) => ({...f, fieldId: v }))}
            testId="filter-field"
          >
            <option value="">Alle Flächen</option>
            {uniqueFields.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </FilterSelect>
          <FilterSelect
            label="Kultur"
            value={filters.kultur}
            onChange={(v) => setFilters((f) => ({...f, kultur: v }))}
            testId="filter-kultur"
          >
            <option value="">Alle Kulturen</option>
            {uniqueKulturen.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </FilterSelect>
          <FilterSelect
            label="Verwendungsart"
            value={filters.verwendungsart}
            onChange={(v) => setFilters((f) => ({...f, verwendungsart: v }))}
            testId="filter-verwendungsart"
          >
            <option value="">Alle</option>
            <option value="freiland">Freiland</option>
            <option value="gewaechshaus">Gewächshaus</option>
            <option value="saatgutbehandlung">Saatgutbehandlung</option>
          </FilterSelect>
          <div />
          <FilterDate
            label="Datum von"
            value={filters.dateFrom}
            onChange={(v) => setFilters((f) => ({...f, dateFrom: v }))}
            testId="filter-from"
          />
          <FilterDate
            label="Datum bis"
            value={filters.dateTo}
            onChange={(v) => setFilters((f) => ({...f, dateTo: v }))}
            testId="filter-to"
          />
        </div>
      )}

      {/* Bulk-Action-Bar */}
      {selected.size > 0 && (
        <div
          className="sticky top-4 z-10 mb-4 bg-forest-900 text-white rounded-2xl shadow-lg px-5 py-3 flex flex-wrap items-center justify-between gap-3"
          data-testid="apps-bulk-bar"
        >
          <div className="flex items-center gap-3">
            <Check size={18} weight="bold" className="text-lime-300" />
            <span className="font-body text-sm">
              {selected.size} ausgewählt
            </span>
            <button
              onClick={() => setSelected(new Set())}
              className="text-xs text-white/70 hover:text-white underline"
            >
              Auswahl aufheben
            </button>
          </div>
          <div className="flex gap-2">
            <button
              onClick={exportPdf}
              data-testid="bulk-export-pdf"
              className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white px-3 py-1.5 rounded-lg text-sm transition"
            >
              <FilePdf size={14} /> PDF
            </button>
            <button
              onClick={exportJson}
              data-testid="bulk-export-json"
              className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white px-3 py-1.5 rounded-lg text-sm transition"
            >
              <FileText size={14} /> JSON
            </button>
            <button
              onClick={bulkDelete}
              data-testid="bulk-delete"
              className="inline-flex items-center gap-1.5 bg-red-500/80 hover:bg-red-500 text-white px-3 py-1.5 rounded-lg text-sm transition"
            >
              <Trash size={14} /> Löschen
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="font-body text-sm text-[#7A827C]" data-testid="apps-loading">
          Lade …
        </div>
      ) : filtered.length === 0 ? (
        <div
          className="bg-white rounded-3xl border border-bone-200 p-12 text-center"
          data-testid="apps-empty"
        >
          <Drop size={28} weight="duotone" className="text-forest-700 mx-auto mb-3" />
          <div className="font-heading text-lg font-semibold text-forest-900">
            {items.length === 0 ? "Noch keine Aufzeichnungen" : "Keine Treffer"}
          </div>
          <div className="font-body text-sm text-[#4A524C] mt-1">
            {items.length === 0
              ? "Erfassen Sie Ihre erste PSM-Anwendung."
              : "Versuchen Sie einen anderen Suchbegriff oder passen Sie die Filter an."}
          </div>
        </div>
      ) : (
        <>
          {/* Header mit Select-All */}
          <div className="flex items-center gap-3 px-5 pb-2">
            <label className="inline-flex items-center gap-2 cursor-pointer group">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={toggleSelectAll}
                data-testid="apps-select-all"
                className="w-4 h-4 rounded border-bone-300 text-forest-600 focus:ring-forest-600/30 cursor-pointer"
              />
              <span className="font-body text-xs uppercase tracking-wider text-[#7A827C] group-hover:text-forest-700 transition">
                {allSelected
                  ? "Auswahl aufheben"
                  : `Alle ${filtered.length} auswählen`}
              </span>
            </label>
            <span className="ml-auto font-body text-xs text-[#7A827C]">
              {filtered.length} von {items.length}
            </span>
          </div>

          <ul className="space-y-3" data-testid="apps-list">
            {filtered.map((a) => {
              const isSelected = selected.has(a.id);
              return (
                <li
                  key={a.id}
                  data-testid={`apps-row-${a.id}`}
                  onClick={() => navigate(`/app/applications/${a.id}`)}
                  className={`group bg-white rounded-2xl border p-5 flex items-start gap-3 cursor-pointer transition ${
                    isSelected
                      ? "border-forest-600 shadow-sm bg-forest-600/5"
                      : "border-bone-200 hover:border-forest-600/40 hover:shadow-sm"
                  }`}
                >
                  <label
                    onClick={(e) => e.stopPropagation()}
                    className="flex items-center pt-1 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => toggleSelected(a.id, e)}
                      data-testid={`apps-select-${a.id}`}
                      className="w-4 h-4 rounded border-bone-300 text-forest-600 focus:ring-forest-600/30 cursor-pointer"
                    />
                  </label>
                  <div className="w-11 h-11 rounded-xl bg-terracotta/10 border border-terracotta/20 flex items-center justify-center flex-shrink-0">
                    <Drop size={20} weight="duotone" className="text-terracotta" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                      <div className="font-heading text-lg font-semibold text-forest-900 truncate group-hover:text-forest-700 transition">
                        {a.product_name}
                      </div>
                      <div className="font-body text-xs text-[#7A827C]">
                        {new Date(a.applied_at).toLocaleString("de-DE")}
                      </div>
                    </div>
                    <div className="font-body text-sm text-[#4A524C] flex flex-wrap gap-x-4 gap-y-1 mt-1">
                      <span>{a.field_name}</span>
                      <span>
                        {a.dosage} {a.dosage_unit}
                      </span>
                      {a.active_ingredient && <span>{a.active_ingredient}</span>}
                      {a.target_pest && <span>{a.target_pest}</span>}
                      <span className="text-[#7A827C]">durch {a.user_name}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={(e) => onDelete(a.id, e)}
                      data-testid={`apps-delete-${a.id}`}
                      className="p-2 text-[#7A827C] hover:text-red-600 transition"
                      aria-label="Löschen"
                    >
                      <Trash size={18} />
                    </button>
                    <CaretRight
                      size={18}
                      className="text-[#B8BCB9] group-hover:text-forest-700 transition"
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}

const FilterSelect = ({ label, value, onChange, testId, children }) => (
  <div>
    <label className="block font-body text-xs uppercase tracking-wider text-[#7A827C] mb-1.5">
      {label}
    </label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className="w-full font-body text-sm px-3 py-2 rounded-xl border border-bone-200 bg-bone focus:outline-none focus:border-forest-600 focus:ring-2 focus:ring-forest-600/15"
    >
      {children}
    </select>
  </div>
);

const FilterDate = ({ label, value, onChange, testId }) => (
  <div>
    <label className="block font-body text-xs uppercase tracking-wider text-[#7A827C] mb-1.5">
      {label}
    </label>
    <input
      type="date"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className="w-full font-body text-sm px-3 py-2 rounded-xl border border-bone-200 bg-bone focus:outline-none focus:border-forest-600 focus:ring-2 focus:ring-forest-600/15"
    />
  </div>
);
